import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CoreGoogleAnalyticActions } from '@fantaz/core/analytic';
import { CoreNotificationActions } from '@fantaz/core/state';
import { environment } from '@fantaz/shared/app-config';
import { AuthActions } from '@fantaz/shared/auth';
import {
  ACCOUNTING_ACCOUNTS,
  APP_MESSAGES,
  NOTIFICATION_TYPES,
  VERIFICATION_TYPES,
  WEBSOCKET_NOTIFICATION_TYPE,
} from '@fantaz/shared/config/static';
import {
  CurrentUser,
  GAEventKeys,
  WebsocketMessage,
  WebsocketMessageBody,
} from '@fantaz/shared/data-access/models';
import { ToastService } from '@fantaz/shared/utils/toast';
import { Store } from '@ngrx/store';
import * as VerifyUserActions from 'libs/user-verification/data-access/src/lib/+state/verify-user/verify-user.actions';
import { Observable } from 'rxjs/internal/Observable';
import {
  VerificationDuplicateException,
  VerificationEscalateException,
  VerificationIncompleteException,
  VerificationRejectedException,
} from './account';
import { FzZendeskService } from './fz-zenddesk.service';
import { WebsocketService } from './websocket.service';

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  user$: Observable<CurrentUser | null> = new Observable<CurrentUser>();
  private wsConnected = false;

  constructor(
    public websocketService: WebsocketService,
    public fzZendeskService: FzZendeskService,
    private toastService: ToastService,
    private router: Router,
    private route: ActivatedRoute,
    private store: Store,
  ) {}

  init(
    messageCallback: (message: any, progress?: number) => void,
    skipTypes?: string[],
  ) {
    let skip: string[] = [];
    if (skipTypes && Array.isArray(skipTypes)) {
      skip = skipTypes;
      skip.push('PLAY_INSTANCE_FINALIZED');
      skip.push('DEPOSIT');
      skip.push('ADMIN_DEPOSIT');
      skip.push('WITHDRAW');
      skip.push('REQUEST_WITHDRAW');
      skip.push('ADMIN_WITHDRAW');
      skip.push('ADMIN_DECLINED_WITHDRAW');
      skip.push('ADMIN_APPROVED_WITHDRAW');
    }
    if (!this.wsConnected) {
      this.websocketService.connect(environment.webSocketURL);
      this.websocketService.on()?.subscribe({
        next: (message: any) => {
          this.onMessageReceive(message, skip);
          messageCallback(message);
        },
        error: (e) => {
          console.error(e);
        },
        complete: () => {
          this.wsConnected = true;
          console.log('complete');
        },
      });
    }
  }

  handleCustomerRegistrationFlow(messageBody: WebsocketMessageBody) {
    switch (messageBody.message) {
      case VERIFICATION_TYPES.DUPLICATE:
        this.store.dispatch(
          VerifyUserActions.verifyUserFailure({
            error: new VerificationDuplicateException(
              [], // TODO: Pass profile matching data
            ),
          }),
        );
        break;
      case VERIFICATION_TYPES.REJECTED:
        this.store.dispatch(
          VerifyUserActions.verifyUserFailure({
            error: new VerificationRejectedException(),
          }),
        );
        break;
      case VERIFICATION_TYPES.ESCALATE:
        this.store.dispatch(
          VerifyUserActions.verifyUserFailure({
            error: new VerificationEscalateException(),
          }),
        );
        break;
      case VERIFICATION_TYPES.INCOMPLETE:
        this.store.dispatch(
          VerifyUserActions.verifyUserFailure({
            error: new VerificationIncompleteException(),
          }),
        );
        break;
      default:
        break;
    }
  }

  getLocationPath(): string {
    const url = new URL(window.location.href);
    return url.pathname;
  }

  onMessageReceive(message: WebsocketMessage, skipTypes: string[]) {
    const body = message.body;
    if (body && body.type) {
      if (body.amount) {
        this.store.dispatch(AuthActions.loadWallet());
      }
      this.store.dispatch(CoreNotificationActions.messageReceived({ message }));
      if (body.type === NOTIFICATION_TYPES.CUSTOMER_REGISTRATION) {
        this.handleCustomerRegistrationFlow(body);
      } else if (
        body.type === NOTIFICATION_TYPES.VERIFICATION_APPROVED ||
        body.type === NOTIFICATION_TYPES.VERIFICATION_REJECTED
      ) {
        this.handleDocumentVerification(body);
      } else {
        if (APP_MESSAGES[body.type]) {
          if (body.type == 'DEPOSIT') {
            this.sendUserPurchaseEventGTM(body);
          } else if (body.type == 'WITHDRAW') {
            this.sendUserWithdrawEventGTM();
          }

          const path = this.getLocationPath();
          if (!skipTypes.includes(body.type)) {
            this.showSnackBarNotification(path, body.type, body.amount);
          }
        }
      }
    }
  }

  private handleDocumentVerification(body: WebsocketMessageBody) {
    if (body.type === NOTIFICATION_TYPES.VERIFICATION_REJECTED) {
      this.store.dispatch(
        CoreGoogleAnalyticActions.trackEvent({
          event: {
            event: GAEventKeys.USER_VERIFICATION_STATUS,
            verification_action: 'user-verification-fail',
            verification_status: 'id-user-verification-fail',
            verification_attempts_failed: 1, //not access to the user information, set 1 as max attempts by default
          },
        }),
      );
    } else {
      this.store.dispatch(
        CoreGoogleAnalyticActions.trackEvent({
          event: {
            event: GAEventKeys.USER_VERIFICATION_STATUS,
            verification_action: 'user-verified-success',
            verification_status: 'id-user-verified-success',
          },
        }),
      );
    }
    const action =
      body.type === NOTIFICATION_TYPES.VERIFICATION_APPROVED
        ? 'CHECK OUT OUR TOURNAMENTS'
        : 'CONTACT CUSTOMER SUPPORT';
    const ref = this.toastService.openSnackBar(APP_MESSAGES[body.type || ''], {
      action,
    });
    ref.onAction().subscribe(() => {
      if (body.type === NOTIFICATION_TYPES.VERIFICATION_APPROVED) {
        window.location.href = '/tournaments';
      } else {
        this.fzZendeskService.openZendesk();
      }
    });
  }

  private getNotificationMessage(
    notificationType: string,
    amount?: string,
  ): string {
    let message = APP_MESSAGES[notificationType];
    const replaceIt = '[AMOUNT]';
    if (message.includes(replaceIt)) {
      message = APP_MESSAGES[notificationType].replace(
        replaceIt,
        amount ? `${Number(amount).toFixed(2)} USD` : 'money', // fallback if the amount is null
      );
    }
    return message;
  }

  private showSnackBarNotification(
    path: string,
    type: string,
    amount?: string,
  ) {
    const message = this.getNotificationMessage(type, amount);
    if (
      !path.includes('/wallet/history') &&
      !path.includes('/wallet/withdraw') &&
      [
        WEBSOCKET_NOTIFICATION_TYPE.DEPOSIT,
        WEBSOCKET_NOTIFICATION_TYPE.WITHDRAW,
        WEBSOCKET_NOTIFICATION_TYPE.ADMIN_DECLINED_WITHDRAW,
        WEBSOCKET_NOTIFICATION_TYPE.ADMIN_APPROVED_WITHDRAW,
      ].includes(type)
    ) {
      if (WEBSOCKET_NOTIFICATION_TYPE.ADMIN_APPROVED_WITHDRAW !== type) {
        this.openSnackBarAndNavigate(path, message, 'SEE TRANSACTION HISTORY', [
          'wallet',
          'history',
        ]);
      } else {
        this.openSnackBarAndNavigate(
          path,
          message,
          'SEE WITHDRAWALS IN PROCESS',
          ['wallet', 'withdraw'],
        );
      }
    } else if (WEBSOCKET_NOTIFICATION_TYPE.REQUEST_WITHDRAW !== type) {
      this.toastService.openSnackBar(message);
    }
  }

  private navigate(path: string, route: string[]) {
    if (path.includes('/wallet')) {
      this.router.navigate(
        route.filter((el) => el !== 'wallet'),
        {
          relativeTo: this.route.parent,
        },
      );
    } else {
      window.location.href = `/${route.join('/')}`;
    }
  }

  private openSnackBarAndNavigate(
    path: string,
    message: string,
    action: string,
    route: string[],
  ) {
    this.toastService
      .openSnackBar(message, { action })
      .onAction()
      .subscribe(() => this.navigate(path, route));
  }

  closeSocket() {
    this.websocketService.close();
  }

  sendUserPurchaseEventGTM(notification: WebsocketMessageBody) {
    const amountParsed = parseFloat(notification.amount || '0');
    let totalAmount = 0;
    let depositType = '';
    let tax = 0;
    const { accountCode } = notification;
    switch (Number(accountCode)) {
      case ACCOUNTING_ACCOUNTS.ACTUM_PROCESSED_DEPOSITS:
        depositType = 'ACTUM';
        totalAmount = (amountParsed * 100) / 105;
        tax = totalAmount * 0.05;
        break;
      case ACCOUNTING_ACCOUNTS.WORLDPAY_PROCESSED_DEPOSITS:
        totalAmount = amountParsed;
        depositType = 'WORLDPAY';
        tax = totalAmount * 0.05;
        break;
      default:
        totalAmount = amountParsed;
        depositType = 'OTHER';
        break;
    }

    this.store.dispatch(
      CoreGoogleAnalyticActions.trackEvent({
        event: {
          event: GAEventKeys.PURCHASE,
          ecommerce: {
            transaction_id: notification.transactionId,
            value: totalAmount,
            tax,
            affiliation: depositType,
            currency: 'USD',
            items: [
              {
                item_name: 'Fantaz Token',
                item_id: Date.now(),
                price: amountParsed,
                item_category: depositType,
              },
            ],
          },
        },
      }),
    );
    this.store.dispatch(
      CoreGoogleAnalyticActions.trackEvent({
        event: {
          event: GAEventKeys.USER_COMPLETED_FUNDS_Z_WALLET,
          founded: amountParsed,
          timestamp: Date.now(),
          transaction_fee_or_taxes: tax,
        },
      }),
    );
  }

  sendUserWithdrawEventGTM() {
    this.store.dispatch(
      CoreGoogleAnalyticActions.trackEvent({
        event: {
          event: GAEventKeys.USER_WITHDRAWLS_MONEY,
          user_withdraw_action: 'user_completes_withdraw',
          user_withdraw_status: 'user_completes_withdraw',
        },
      }),
    );
  }
}
